import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import AccessibilityPanel from "./AccessibilityPanel";
import { useAccessibility } from "./hooks/useAccessibility";
import { TRANSLATIONS } from "./config/settings";
import { LangContext } from "../context/LangContext";
import "./accessibility.css";

function WidgetInner() {
  const [open, setOpen] = useState(false);
  const { settings } = useAccessibility();
  const langCtx = useContext(LangContext);
  const t = TRANSLATIONS[settings.language] || TRANSLATIONS.en;
  const toggleRef = useRef(null);

  useEffect(() => {
    if (settings.language === "ne") {
      langCtx.dispatch({ type: "NEPALI" });
    } else {
      langCtx.dispatch({ type: "ENGLISH" });
    }
  }, [settings.language]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => toggleRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (!open) return;
      const panel = document.querySelector(".pb-a11y-panel");
      const toggle = document.querySelector(".pb-a11y-toggle");
      if (
        panel &&
        !panel.contains(e.target) &&
        toggle &&
        !toggle.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  return (
    <>
      <button
        ref={toggleRef}
        className={"pb-a11y-toggle" + (open ? " pb-a11y-toggle--open" : "")}
        onClick={handleToggle}
        aria-label={t.toggleButton}
        aria-expanded={open}
        type="button"
      >
        <img
          className="pb-a11y-toggle-icon"
          src="/accessibility-icon.png"
          alt={t.toggleButton}
        />
      </button>

      {open &&
        createPortal(
          <div
            className="pb-a11y-backdrop"
            onClick={handleClose}
            aria-hidden="true"
          />,
          document.body,
        )}

      {open &&
        createPortal(
          <AccessibilityPanel onClose={handleClose} />,
          document.body,
        )}
    </>
  );
}

export default function AccessibilityWidget() {
  return (
    <AccessibilityProvider>
      <WidgetInner />
    </AccessibilityProvider>
  );
}
